import apiClient from '../../api';
import { isArr } from '@/utils';
import initialState from '../initialState';
import store from '..';

const pepperApi = apiClient.merchy.pepper;

/**
 * @description Is valid
 * @param pepperData
 * @returns {boolean}
 */
const isValid = (pepperData) => {
  const { merchants, groups, events } = pepperData;

  return isArr(merchants) && isArr(groups) && isArr(events);
};

/**
 * @description Init state
 * @param initialState
 * @returns {*}
 */
const initState = (initialState) => {
  if (!isValid(initialState)) {
    throw Error('Invalid initial pepper state');
  }

  const { merchants, groups, events } = initialState;
  return {
    merchants,
    groups,
    events,
  };
};

/**
 * @description Getters
 * @type {*}
 */
export const getters = {
  merchants: ({ merchants }) => merchants,
  groups: ({ groups }) => groups,
  events: ({ events }) => events,
};

const actions = {
  clearMerchants: ({ commit, state }) => commit('SET', {
    ...state,
    merchants: [],
  }),
  clearGroups: ({ commit, state }) => commit('SET', {
    ...state,
    groups: [],
  }),
  getThread: (context, query) =>
    pepperApi.getThread(query)
      .then((res) => res.data.data),
  getThreads: (context, query) =>
    pepperApi.getThreads(query)
      .then((res) => res.data.data),
  getMerchants: ({ commit, state }, query) => {
    store.dispatch('pepper/clearMerchants');
    return pepperApi.getMerchants(query)
      .then((res) => {
        const nextItems = res.data.data;

        const nextState = {
          ...state,
          merchants: nextItems,
        };

        commit('SET', nextState);
        return nextItems;
      }).catch(e => Promise.reject(e));
  },
  /**
   * Looks up merchants by id and adds them to the cached listing rather than
   * replacing it, which is what getMerchants does. The merchant dropdown reads
   * its options straight from this state, so a merchant restored from a URL has
   * to end up here or the select has a value it cannot offer.
   *
   * A merchant id is only unique within one Pepper platform (518 is Samsung on
   * FR and KICKZ on DE), so pass `countries` whenever the caller knows them.
   */
  getMerchantsByIds: ({ commit, state }, { ids, countries = [] }) => (
    Promise.all(ids.map((id) => pepperApi.getMerchants({
      merchant_id: id,
      ...(countries.length ? { countries } : {}),
    })))
      .then((responses) => {
        const found = responses.flatMap((res) => res.data.data);
        const keyOf = ({ id, country }) => `${id}:${country?.code}`;
        const known = new Set(state.merchants.map(keyOf));
        const nextMerchants = [...state.merchants, ...found.filter((merchant) => !known.has(keyOf(merchant)))];

        commit('SET', { ...state, merchants: nextMerchants });
        return found;
      })
  ),
  /**
   * Looks a group up by id and returns it without committing: the cached
   * `groups` listing belongs to whatever the user last searched.
   */
  getGroupsByIds: (context, { ids }) => (
    Promise.all(ids.map((id) => pepperApi.getGroups({ id })))
      .then((responses) => responses.flatMap((res) => res.data.data))
  ),
  getGroups: ({ commit, state }, query) => {
    store.dispatch('pepper/clearGroups');
    return pepperApi.getGroups(query)
      .then((res) => {
        const { data } = res.data;
        const nextItems = {
          groups: data,
        };

        const nextState = {
          ...state,
          ...nextItems,
        };

        commit('SET', nextState);
        return nextItems;
      }).catch(e => Promise.reject(e));
  },
  getEvents: ({ commit, state }, query) =>
    pepperApi.getEvents(query).then((res) => {
      const nextEvents = res.data.data;

      const nextState = {
        ...state,
        events: nextEvents,
      };

      commit('SET', nextState);

      return nextEvents;
    }),
  reset: ({ commit }) => commit('SET', initialState.pepper),
  set: ({ commit }, pepperData) => {
    commit('SET', pepperData);
  },
};

const mutations = {
  SET(state, pepper) {
    /* eslint-disable no-param-reassign */
    state.merchants = pepper.merchants;
    state.groups = pepper.groups;
    state.events = pepper.events;
  },
};

export default (initialState) => ({
  namespaced: true,
  state: initState(initialState),
  getters,
  actions,
  mutations,
});
